import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import useApproval from '@/hooks/approval/useApproval';
import approvalDetailToFormState from '@/utils/approval/approvalDetailToFormState';
import { useSnackbar } from '@/hooks/global-tools';

/**
 * 모달(ApprovalModal/CreateApprovalModal) 조회→편집 전환 상태 + 편집 폼 preload.
 * preload 실패 시 편집 모드를 해제해 모달 본문이 빈 화면으로 남지 않게 한다. (PORTAL-13208)
 */
const useApprovalEditMode = (documentId?: string | null) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [isEditMode, setIsEditMode] = useState(false);
  // 직전 실패의 error 가 남아 있으면 편집 진입당 1회만 명시 재요청 — 재요청 실패가
  // 다시 이 effect 로 돌아와 무한 재시도되는 것을 막는 가드.
  const retriedRef = useRef(false);

  // 편집 폼 즉시 진입용 v2 단건 preload — useApprovalView 와 캐시 키가 달라 별도 호출
  const { data: detail, error, isLoading, mutate } = useApproval(documentId);
  const initialFormState = detail
    ? approvalDetailToFormState(detail)
    : undefined;

  // 조회 복귀 대상은 최초 로드 실패뿐 — stale 데이터가 있으면 재검증 실패여도 편집 유지.
  // 재시도 in-flight(isLoading)면 로딩 유지 — 결과 확정 후 판정.
  // 복귀로 isEditMode 가 꺼지므로 토스트는 편집 진입당 1회(SWR 백그라운드 재시도와 무관).
  useEffect(() => {
    if (!isEditMode) {
      retriedRef.current = false;
      return;
    }
    if (detail || isLoading) return;
    if (error && !retriedRef.current) {
      retriedRef.current = true;
      void mutate();
      return;
    }
    // detail === undefined 는 아직 로드 전, null 은 200 + data:null 로 확정된 빈 응답(삭제·권한 등).
    if (!error && detail === undefined) return;
    setIsEditMode(false);
    showError({
      message: t('편집 화면을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.'),
    });
  }, [isEditMode, detail, error, isLoading, mutate, showError, t]);

  return { isEditMode, setIsEditMode, initialFormState };
};

const LoadingBox = styled('div')`
  height: 100%;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EditModeLoading = () => (
  <LoadingBox>
    <Spinner theme="black" />
  </LoadingBox>
);

export default useApprovalEditMode;
